import { type ColumnConfig } from '../components/ColumnSettings';
import type { Campaign } from '../api/campaigns';
import { fetchUserSettings, updateUserSettings } from '../api/userSettings';

export interface TableColumn extends ColumnConfig {
  key: keyof Campaign | 'actions' | 'status' | 'last_edited';
  editable?: boolean;
  type?: 'text' | 'date' | 'url' | 'boolean';
}

// Default/min/max column widths in pixels (used by resizable columns)
export const DEFAULT_COLUMN_WIDTH = 160;
export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 600;

export const DEFAULT_COLUMNS: TableColumn[] = [
  { id: 'actions', key: 'actions', label: 'Actions', visible: true, locked: true, width: 90 },
  { id: 'status', key: 'status', label: 'Status', visible: true, locked: true, width: 200 },
  {
    id: 'campaign_id',
    key: 'campaign_id',
    label: 'Campaign ID',
    visible: true,
    locked: true,
    width: 200,
  },
  { id: 'platform', key: 'platform', label: 'Platform', visible: true, width: 120 },
  { id: 'quarter', key: 'quarter', label: 'Quarter', visible: true, width: 100 },
  { id: 'market', key: 'market', label: 'Market', visible: true, width: 110 },
  { id: 'business_unit', key: 'business_unit', label: 'Business Unit', visible: true, width: 150 },
  { id: 'nameplate', key: 'nameplate', label: 'Nameplate', visible: true, width: 140 },
  { id: 'buy_type', key: 'buy_type', label: 'Buy Type', visible: true, width: 130 },
  { id: 'tier', key: 'tier', label: 'Tier', visible: true, width: 100 },
  { id: 'placement', key: 'placement', label: 'Placement', visible: true, width: 160 },
  { id: 'format', key: 'format', label: 'Format', visible: true, width: 160 },
  { id: 'size', key: 'size', label: 'Size', visible: true, width: 120 },
  { id: 'video_length', key: 'video_length', label: 'Video Length', visible: true, width: 120 },
  { id: 'language', key: 'language', label: 'Language', visible: true, width: 120 },
  { id: 'price_assets', key: 'price_assets', label: 'Price Assets', visible: true, width: 130 },
  {
    id: 'start_date',
    key: 'start_date',
    label: 'Start Date ✎',
    visible: true,
    editable: true,
    type: 'date',
    width: 140,
  },
  {
    id: 'end_date',
    key: 'end_date',
    label: 'End Date ✎',
    visible: true,
    editable: true,
    type: 'date',
    width: 140,
  },
  {
    id: 'fordshare_entries',
    key: 'fordshare_entries',
    label: 'FordShare IDs',
    visible: true,
    width: 140,
  },
  {
    id: 'preview_link',
    key: 'preview_link',
    label: 'Preview Link ✎',
    visible: true,
    editable: true,
    type: 'url',
    width: 160,
  },
  {
    id: 'live',
    key: 'live',
    label: 'Live ✎',
    visible: true,
    editable: true,
    type: 'boolean',
    width: 100,
  },
  {
    id: 'live_date',
    key: 'live_date',
    label: 'Live Date ✎',
    visible: true,
    editable: true,
    type: 'date',
    width: 140,
  },
  {
    id: 'last_edited',
    key: 'last_edited',
    label: 'Last Edited',
    visible: true,
    locked: true,
    width: 180,
  },
];

// Team-specific column visibility configurations
export const VALID_TEAM_NAMES = [
  'Mindshare Planning',
  'VML Poland',
  'Creative',
  'Activation',
] as const;
export type TeamName = (typeof VALID_TEAM_NAMES)[number];

// Column IDs to hide for each team
const TEAM_HIDDEN_COLUMNS: Record<TeamName, string[]> = {
  'Mindshare Planning': [],
  'VML Poland': ['placement', 'live_date', 'preview_link'],
  Creative: [],
  Activation: [],
};

/**
 * Get team-specific default columns
 */
export function getTeamDefaultColumns(team: TeamName): TableColumn[] {
  const hiddenColumns = TEAM_HIDDEN_COLUMNS[team];
  return DEFAULT_COLUMNS.map((col) => ({
    ...col,
    visible: col.locked ? true : !hiddenColumns.includes(col.id) && col.visible,
  }));
}

const STORAGE_KEY = 'content-tracker-column-settings';
const TEAM_STORAGE_KEY = 'content-tracker-selected-team';

// Debounce timers for backend saves
let columnSettingsSaveTimer: ReturnType<typeof setTimeout> | null = null;
let teamSelectionSaveTimer: ReturnType<typeof setTimeout> | null = null;
const BACKEND_SAVE_DELAY_MS = 500;

/**
 * Validates that an object conforms to the ColumnConfig interface.
 *
 * @param obj - Object to validate
 * @returns True if object has required ColumnConfig properties
 */
function isValidColumnConfig(obj: unknown): obj is ColumnConfig {
  if (typeof obj !== 'object' || obj === null) return false;
  const col = obj as Record<string, unknown>;
  return (
    typeof col.id === 'string' &&
    typeof col.label === 'string' &&
    typeof col.visible === 'boolean' &&
    (col.locked === undefined || typeof col.locked === 'boolean') &&
    (col.width === undefined || typeof col.width === 'number')
  );
}

/**
 * Saves the selected team to both backend and localStorage.
 *
 * Uses immediate localStorage save and debounced backend save to prevent
 * race conditions when users make rapid successive changes.
 */
export function saveSelectedTeam(team: TeamName | null): void {
  // Save to localStorage immediately
  try {
    if (team) {
      localStorage.setItem(TEAM_STORAGE_KEY, team);
    } else {
      localStorage.removeItem(TEAM_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to save selected team to localStorage:', error);
  }

  // Debounce backend save to prevent race conditions
  if (teamSelectionSaveTimer !== null) {
    clearTimeout(teamSelectionSaveTimer);
  }

  teamSelectionSaveTimer = setTimeout(() => {
    updateUserSettings({ selected_team: team }).catch((error) => {
      console.warn('Failed to save selected team to backend:', error);
    });
  }, BACKEND_SAVE_DELAY_MS);
}

/**
 * Loads the selected team from localStorage (synchronous).
 */
export function loadSelectedTeam(): TeamName | null {
  try {
    const saved = localStorage.getItem(TEAM_STORAGE_KEY);
    if (saved && VALID_TEAM_NAMES.includes(saved as TeamName)) {
      return saved as TeamName;
    }
    return null;
  } catch (error) {
    console.error('Failed to load selected team:', error);
    return null;
  }
}

/**
 * Async version that tries backend first, then falls back to localStorage.
 */
export async function loadSelectedTeamAsync(): Promise<TeamName | null> {
  try {
    const settings = await fetchUserSettings();
    if (settings.selected_team) {
      const team = settings.selected_team;
      if (VALID_TEAM_NAMES.includes(team as TeamName)) {
        // Sync to localStorage
        try {
          localStorage.setItem(TEAM_STORAGE_KEY, team);
        } catch (e) {
          console.warn('Failed to sync team to localStorage:', e);
        }
        return team as TeamName;
      }
    }
  } catch (error) {
    console.warn('Failed to load team from backend, using localStorage:', error);
  }

  // Fallback to localStorage
  return loadSelectedTeam();
}

/**
 * Applies team-specific default column settings and persists them.
 *
 * This will override current column settings with the defaults for the given team,
 * save the resulting column configuration, and save the selected team itself.
 * Both the column settings and the selected team are persisted to localStorage
 * and to the backend (via {@link saveColumnSettings} and {@link saveSelectedTeam}).
 *
 * @param team - The team name to apply defaults for.
 * @returns void - Column settings and selected team are saved to both localStorage and backend.
 */
export function applyTeamDefaults(team: TeamName): void {
  const teamColumns = getTeamDefaultColumns(team);
  saveColumnSettings(teamColumns);
  saveSelectedTeam(team);
}

/**
 * Saves column settings to both backend and localStorage.
 *
 * Uses hybrid approach:
 * - Saves to localStorage immediately (synchronous, fast)
 * - Debounces backend save to prevent race conditions from rapid successive changes
 *
 * Note: Failures are logged but silently ignored. Column settings will still work
 * via localStorage even if backend save fails.
 *
 * @param columns - Array of column configurations to save
 */
export function saveColumnSettings(columns: ColumnConfig[]): void {
  // Save to localStorage immediately (synchronous, fast)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch (error) {
    console.error('Failed to save column settings to localStorage:', error);
  }

  // Debounce backend save to prevent race conditions
  if (columnSettingsSaveTimer !== null) {
    clearTimeout(columnSettingsSaveTimer);
  }

  columnSettingsSaveTimer = setTimeout(() => {
    updateUserSettings({ column_settings: columns }).catch((error) => {
      console.warn('Failed to save column settings to backend:', error);
      // Not a critical error - localStorage will still work
    });
  }, BACKEND_SAVE_DELAY_MS);
}

/**
 * Loads column settings with hybrid approach (backend first, localStorage fallback).
 *
 * Loading Strategy:
 * 1. Try to load from backend (for cross-device sync)
 * 2. If backend fails, fallback to localStorage
 * 3. If both fail, use DEFAULT_COLUMNS
 *
 * This function handles three scenarios:
 * 1. No saved settings: Returns DEFAULT_COLUMNS
 * 2. Saved settings exist: Merges with DEFAULT_COLUMNS to preserve new properties
 * 3. New columns added: Appends new columns to the end of the saved order
 *
 * Merge Strategy:
 * - Preserves saved visibility settings for existing columns
 * - Keeps all TableColumn properties (key, editable, type) from DEFAULT_COLUMNS
 * - Maintains user's column order from saved settings
 * - Appends any new columns (not in saved settings) at the end
 * - Removes any saved columns that no longer exist in DEFAULT_COLUMNS
 *
 * Example:
 * - DEFAULT_COLUMNS: [A, B, C, D]
 * - Saved settings: [B(hidden), A, C]
 * - Result: [B(hidden), A, C, D] - D is new, appended at end
 *
 * @returns Array of TableColumn configurations with merged settings
 */
export function loadColumnSettings(): TableColumn[] {
  // This function is synchronous, so we use localStorage as the primary source
  // The async backend sync happens on component mount via loadColumnSettingsAsync
  return loadColumnSettingsFromStorage();
}

/**
 * Async version that tries backend first, then falls back to localStorage.
 * Use this in components that can await the result.
 */
export async function loadColumnSettingsAsync(): Promise<TableColumn[]> {
  try {
    // Try to load from backend first
    const settings = await fetchUserSettings();
    if (settings.column_settings && Array.isArray(settings.column_settings)) {
      const savedColumns = settings.column_settings as ColumnConfig[];

      // Validate the data
      if (savedColumns.every(isValidColumnConfig)) {
        // Sync to localStorage for offline support
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(savedColumns));
        } catch (e) {
          console.warn('Failed to sync backend settings to localStorage:', e);
        }

        // Merge with defaults and return
        return mergeColumnSettings(savedColumns);
      }
    }
  } catch (error) {
    console.warn('Failed to load settings from backend, using localStorage:', error);
  }

  // Fallback to localStorage
  return loadColumnSettingsFromStorage();
}

/**
 * Helper function to load column settings from localStorage only.
 */
function loadColumnSettingsFromStorage(): TableColumn[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return DEFAULT_COLUMNS;

    const parsed = JSON.parse(saved);

    // Validate parsed data
    if (!Array.isArray(parsed) || !parsed.every(isValidColumnConfig)) {
      console.warn('Invalid column settings in localStorage, using defaults');
      return DEFAULT_COLUMNS;
    }

    const savedColumns: ColumnConfig[] = parsed;
    return mergeColumnSettings(savedColumns);
  } catch (error) {
    console.error('Failed to load column settings:', error);
    return DEFAULT_COLUMNS;
  }
}

/**
 * Merge saved column settings with DEFAULT_COLUMNS.
 */
function mergeColumnSettings(savedColumns: ColumnConfig[]): TableColumn[] {
  // Step 1: Merge saved visibility with default column properties
  // This ensures we keep updated properties (key, editable, type) from DEFAULT_COLUMNS
  const merged = DEFAULT_COLUMNS.map((defaultCol) => {
    const saved = savedColumns.find((c) => c.id === defaultCol.id);
    return saved
      ? {
          ...defaultCol,
          visible: saved.visible,
          // Preserve the user's saved width; fall back to the default when the
          // saved settings predate the resizable-columns feature (no width).
          width: saved.width ?? defaultCol.width,
        }
      : defaultCol;
  });

  // Step 2: Apply saved column order
  // Iterate through saved columns and add them in that order
  const ordered: TableColumn[] = [];
  savedColumns.forEach((savedCol) => {
    const col = merged.find((c) => c.id === savedCol.id);
    if (col) ordered.push(col);
  });

  // Step 3: Append any new columns that weren't in saved settings
  // This handles when new columns are added to DEFAULT_COLUMNS
  merged.forEach((col) => {
    if (!ordered.find((c) => c.id === col.id)) {
      ordered.push(col);
    }
  });

  return ordered;
}
